import React, { useEffect, useState } from "react";
import {
  ArrowRight,
  ChevronLeft,
  Clock3,
  ImagePlus,
  MapPin,
  Users,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, upload } from "../api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const Banner = ({ children, tone = "info" }) => (
  <div className={"banner " + tone}>{children}</div>
);

function EventForm() {
  const { eventId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const edit = !!eventId;
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [f, setF] = useState({
    name: "",
    description: "",
    location: "",
    operatingHours: "Today, 9:00 AM – 5:00 PM",
    timezone: "UTC",
    capacity: 50,
    type: "walk_in",
    category: "other",
    visibility: "public",
    venueName: "",
    locationDisplayName: "",
    startsAt: "",
    endsAt: "",
    shortDescription: "",
  });
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({});
  const [draftSaved, setDraftSaved] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const nameError =
    touched.name && !f.name.trim() ? "Listing name is required." : "";
  const capacityError =
    touched.capacity && (!Number(f.capacity) || Number(f.capacity) < 1)
      ? "Enter at least 1 person."
      : "";
  useEffect(() => {
    if (edit)
      api("/events/" + eventId)
        .then((r) =>
          setF({
            ...r.event,
            location: r.event.internalLocation || "",
            locationDisplayName:
              r.event.locationDisplayName || r.event.location || "",
          }),
        )
        .catch((e) => setError(e.message));
  }, [edit, eventId]);
  useEffect(() => {
    if (edit || (!f.name && !f.description)) return undefined;
    const timer = setTimeout(() => {
      localStorage.setItem("queme-new-listing-draft", JSON.stringify(f));
      setDraftSaved(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [edit, f]);
  useEffect(() => {
    if (edit) return;
    const saved = localStorage.getItem("queme-new-listing-draft");
    if (saved) {
      try {
        setF((current) => ({ ...current, ...JSON.parse(saved) }));
      } catch (_) {
        /* ignore invalid drafts */
      }
    }
  }, [edit]);
  async function submit(e) {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const r = await api(edit ? "/events/" + eventId : "/events", {
        method: edit ? "PATCH" : "POST",
        body: JSON.stringify({
          ...f,
          capacity: Number(f.capacity),
          status: "draft",
        }),
      });
      if (imageFile)
        await upload(
          `/events/${r.event.id}/media/coverImage/upload`,
          imageFile,
          "image",
          { tenantId: user?.tenantId },
        );
      toast.success(edit ? "Listing saved." : "Listing created.");
      nav("/manage/events/" + r.event.id + "/queue");
      localStorage.removeItem("queme-new-listing-draft");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }
  function selectImage(file) {
    setImageFile(file || null);
    setImagePreview(file ? URL.createObjectURL(file) : "");
  }
  function updateField(key, value) {
    setF({ ...f, [key]: value });
    setDraftSaved(false);
  }
  const updateHours = (key, value) => {
    const next = { ...f, [key]: value };
    next.operatingHours = `Today, ${next.openingStart || "9:00 AM"} – ${next.openingEnd || "5:00 PM"}`;
    updateField(key, value);
    setF(next);
  };
  const typeLabel =
    {
      walk_in: "Walk-in",
      appointment: "Appointments",
      hybrid: "Hybrid",
      virtual: "Virtual",
      ongoing: "Ongoing",
    }[f.type] || "Walk-in";
  return (
    <div className="page form-page">
      <div className="form-mobile-header">
        <Link className="back-link" to="/manage/events">
          <ChevronLeft size={16} /> <span>New listing</span>
        </Link>
        <span>⋯</span>
      </div>
      <Link className="back-link form-desktop-back" to="/manage/events">
        <ChevronLeft size={16} /> My listings
      </Link>
      <div className="eyebrow">{edit ? "EDIT LISTING" : "CREATE LISTING"}</div>
      <h1>{edit ? "Tune the details." : "Create a public listing."}</h1>
      <p className="muted form-subtitle">
        Create the page customers will use to find and join your queue.
      </p>
      <div className="form-progress">
        <span className="active">Basics</span>
        <i />
        <span>Location</span>
        <i />
        <span>Queue settings</span>
      </div>
      {error && <Banner tone="error">{error}</Banner>}
      <div className="form-workspace">
        <form id="listing-form" className="form-grid" onSubmit={submit}>
          <section className="form-section wide">
            <div className="form-section-heading">
              <div>
                <span className="form-step">1</span>
                <div>
                  <h2>Basics</h2>
                  <p>Tell customers what this listing is for.</p>
                </div>
              </div>
              <span className="form-required-note">* Required</span>
            </div>
            <label>
              Listing name <span className="required-mark">*</span>
              <input
                required
                value={f.name}
                onBlur={() => setTouched({ ...touched, name: true })}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="e.g. Saturday walk-in clinic"
              />
              {nameError && <small className="field-error">{nameError}</small>}
            </label>
            <label>
              Short summary <span className="optional">Optional</span>
              <input
                maxLength="300"
                value={f.shortDescription || ""}
                onChange={(e) =>
                  updateField("shortDescription", e.target.value)
                }
                placeholder="e.g. Same-day consultations with our specialist team"
              />
              <small className="field-hint">
                A one-line summary shown on discovery cards.
              </small>
            </label>
            <label className="wide">
              Description <span className="optional">Optional</span>
              <textarea
                value={f.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Tell customers what to expect before they join."
              />
            </label>
            <label className="wide upload-field">
              Cover image <span className="optional">Optional</span>
              <button
                type="button"
                className="image-dropzone"
                onClick={() =>
                  document.getElementById("cover-image-input").click()
                }
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Cover preview" />
                ) : (
                  <ImagePlus size={24} />
                )}
                <strong>
                  {imageFile ? imageFile.name : "Add a cover image"}
                </strong>
                <span>
                  {imageFile ? "Replace image" : "Drag and drop or browse"}
                </span>
                <small>PNG, JPEG or WebP · max 5 MB</small>
              </button>
              <input
                id="cover-image-input"
                className="visually-hidden"
                type="file"
            accept="image/png,image/jpeg,image/webp"
                onChange={(e) => selectImage(e.target.files?.[0])}
              />
            </label>
            <label>
              Event type <span className="required-mark">*</span>
              <select
                value={f.type || "walk_in"}
                onChange={(e) => updateField("type", e.target.value)}
              >
                <option value="walk_in">Walk-in</option>
                <option value="appointment">Appointments</option>
                <option value="hybrid">Hybrid</option>
                <option value="virtual">Virtual</option>
                <option value="ongoing">Ongoing</option>
              </select>
            </label>
            <label>
              Category <span className="required-mark">*</span>
              <select
                value={f.category || "other"}
                onChange={(e) => updateField("category", e.target.value)}
              >
                {[
                  "other",
                  "healthcare",
                  "restaurant",
                  "government",
                  "banking",
                  "retail",
                  "salon",
                  "education",
                  "service_center",
                  "event",
                ].map((value) => (
                  <option key={value} value={value}>
                    {value.replace("_", " ")}
                  </option>
                ))}
              </select>
            </label>
          </section>
          <section className="form-section wide">
            <div className="form-section-heading">
              <div>
                <span className="form-step">2</span>
                <div>
                  <h2>Location</h2>
                  <p>Tell customers where to go when their turn is ready.</p>
                </div>
              </div>
            </div>
            <label>
              Venue / branch <span className="required-mark">*</span>
              <input
                required
                value={f.venueName || ""}
                onChange={(e) => updateField("venueName", e.target.value)}
                placeholder="Apollo Hospital"
              />
            </label>
            <label>
              Area / internal location{" "}
              <span className="optional">Optional</span>
              <input
                value={f.location}
                onChange={(e) => updateField("location", e.target.value)}
                placeholder="Front desk · Floor 2"
              />
            </label>
            <label className="wide">
              Address <span className="optional">Optional</span>
              <input
                value={f.locationDisplayName || ""}
                onChange={(e) =>
                  updateField("locationDisplayName", e.target.value)
                }
                placeholder="Search or enter address"
              />
            </label>
          </section>
          <section className="form-section wide">
            <div className="form-section-heading">
              <div>
                <span className="form-step">3</span>
                <div>
                  <h2>Queue settings</h2>
                  <p>
                    Control when customers can join and how many people can
                    wait.
                  </p>
                </div>
              </div>
            </div>
            <label>
              Opening hours
              <div className="hours-row">
                <input
                  aria-label="Opening time"
                  type="time"
                  value={f.openingStart || "09:00"}
                  onChange={(e) => updateHours("openingStart", e.target.value)}
                />
                <span>to</span>
                <input
                  aria-label="Closing time"
                  type="time"
                  value={f.openingEnd || "17:00"}
                  onChange={(e) => updateHours("openingEnd", e.target.value)}
                />
              </div>
              <span className="checkbox-label">
                <input
                  type="checkbox"
                  checked={!!f.hoursEveryDay}
                  onChange={(e) =>
                    updateField("hoursEveryDay", e.target.checked)
                  }
                />{" "}
                Use these hours every day
              </span>
            </label>
            <label>
              Maximum people waiting <span className="required-mark">*</span>
              <input
                type="number"
                min="1"
                required
                value={f.capacity}
                onBlur={() => setTouched({ ...touched, capacity: true })}
                onChange={(e) => updateField("capacity", e.target.value)}
              />
              {capacityError ? (
                <small className="field-error">{capacityError}</small>
              ) : (
                <small className="field-hint">
                  New joins pause automatically when this limit is reached.
                </small>
              )}
            </label>
            <fieldset className="joining-fieldset">
              <legend>Joining</legend>
              <label className="radio-label">
                <input
                  type="radio"
                  name="joining"
                  checked={(f.joiningBehavior || "hours") === "hours"}
                  onChange={() => updateField("joiningBehavior", "hours")}
                />{" "}
                Open automatically during opening hours
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="joining"
                  checked={f.joiningBehavior === "manual"}
                  onChange={() => updateField("joiningBehavior", "manual")}
                />{" "}
                Staff opens the queue manually
              </label>
            </fieldset>
            <details
              className="form-advanced wide"
              open={advancedOpen}
              onToggle={(e) => setAdvancedOpen(e.target.open)}
            >
              <summary>
                <span>Advanced scheduling &amp; visibility</span>
                <small>Recurring hours, discoverability and availability</small>
              </summary>
              <div className="form-advanced-grid">
                <label>
                  Public visibility
                  <select
                    value={f.visibility || "public"}
                    onChange={(e) => setF({ ...f, visibility: e.target.value })}
                  >
                    <option value="public">Public discovery</option>
                    <option value="unlisted">Unlisted link</option>
                    <option value="private">Private</option>
                  </select>
                </label>
                <label>
                  Timezone
                  <input
                    value={f.timezone}
                    onChange={(e) => setF({ ...f, timezone: e.target.value })}
                    placeholder="Asia/Kolkata"
                  />
                </label>
                <label>
                  Starts at <span className="optional">Optional</span>
                  <input
                    type="datetime-local"
                    value={f.startsAt ? String(f.startsAt).slice(0, 16) : ""}
                    onChange={(e) =>
                      setF({ ...f, startsAt: e.target.value || null })
                    }
                  />
                </label>
                <label>
                  Ends at <span className="optional">Optional</span>
                  <input
                    type="datetime-local"
                    value={f.endsAt ? String(f.endsAt).slice(0, 16) : ""}
                    onChange={(e) =>
                      setF({ ...f, endsAt: e.target.value || null })
                    }
                  />
                </label>
              </div>
            </details>
          </section>
          <div className="wide">
            <button className="button form-submit" disabled={saving}>
              {saving
                ? "Saving listing…"
                : edit
                  ? "Save listing"
                  : "Create listing"}{" "}
              <ArrowRight size={18} />
            </button>
            <p className="form-submit-note">
              {draftSaved
                ? "Draft saved"
                : "Changes are saved locally while you edit."}
            </p>
          </div>
        </form>
        <aside className="preview-panel">
          <div className="preview-label">LIVE PREVIEW</div>
          <div className="listing-preview-card">
            <div className="preview-cover">
              {imagePreview ? (
                <img src={imagePreview} alt="" />
              ) : (
                <ImagePlus size={24} />
              )}
            </div>
            <div className="preview-content">
              <span className="preview-pill">{typeLabel}</span>
              <h3>{f.name || "Saturday clinic"}</h3>
              <p>
                {f.shortDescription || "Your listing summary will appear here."}
              </p>
              <div>
                <MapPin size={14} /> {f.venueName || "Apollo Hospital"}
              </div>
              <div>
                <Clock3 size={14} />{" "}
                {f.operatingHours || "Today, 9:00 AM – 5:00 PM"}
              </div>
              <div>
                <Users size={14} /> Up to {f.capacity || 50} waiting
              </div>
            </div>
          </div>
          <div className="completion-card">
            <strong>Listing completeness</strong>
            <div className="completion-bar">
              <span
                style={{
                  width: `${Math.min(100, 45 + [f.name, f.shortDescription, f.venueName, f.description].filter(Boolean).length * 12)}%`,
                }}
              />
            </div>
            <span>
              {Math.min(
                100,
                45 +
                  [
                    f.name,
                    f.shortDescription,
                    f.venueName,
                    f.description,
                  ].filter(Boolean).length *
                    12,
              )}
              % complete
            </span>
          </div>
        </aside>
      </div>
      <div className="mobile-form-actions">
        <button
          className="button form-submit"
          form="listing-form"
          disabled={saving}
        >
          {saving ? "Saving…" : edit ? "Save listing" : "Create listing"}{" "}
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}

export default EventForm;
